import React, { useEffect, useState } from 'react';
import { Box, Typography, TextField, InputAdornment, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Grid, Card, CardContent } from '@mui/material';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import AptaTable from 'examples/AptaTable';
import axios from 'axios';
import moment from 'moment';
import { Search, Visibility, Phone, Email, Cake, AccessTime, LocationOn, Close } from '@mui/icons-material';
import { hosturl } from 'constants/constant';

function Users() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await axios.get(`${hosturl}/api/app-users`);
        if (response.data) {
          setUsers(response.data);
        }
      } catch (err) {
        setError('Failed to fetch data');
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, []);

  const handleOpenModal = (user) => {
    setSelectedUser(user);
    setOpen(true);
  };

  const handleCloseModal = () => {
    setOpen(false);
  };

  const filterUsers = () => {
    const filtered = users.filter(user => {
      const username = user.username ? user.username.toLowerCase() : '';
      const phoneNumber = user.phoneNumber ? user.phoneNumber : '';
      return username.includes(searchQuery.toLowerCase()) || phoneNumber.includes(searchQuery);
    });
    return filtered;
  };

  const dateTime = selectedUser?.horoscopeData?.time;
  const formattedTime = dateTime ? new Date(dateTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 'N/A';

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <Box sx={{ padding: 3 }}>
        {/* Search Bar */}
        <Box mb={3} display="flex" justifyContent="space-between" alignItems="center">
          <TextField
            label="Search by Phone, Name"
            variant="outlined"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* Filtered Data Table */}
        {loading ? (
          <Typography variant="h6" color="textSecondary">
            Loading...
          </Typography>
        ) : error ? (
          <Typography variant="h6" color="error">
            {error}
          </Typography>
        ) : (
          <AptaTable
            columns={['username', 'phoneNumber', 'email', 'action']}
            rows={filterUsers().map(user => ({
              username: user.username,
              phoneNumber: user.phoneNumber,
              email: user.email,
              dob: user.horoscopeData && user.horoscopeData[0]
                ? moment(user.horoscopeData?.dob).format('YYYY-MM-DD')
                : 'N/A',
              zodiac: user.horoscopeData && user.horoscopeData[0]
                ? user.horoscopeData?.zodiac
                : 'N/A',
              createdAt: moment(user.createdAt).format('YYYY-MM-DD'),
              action: (
                <Button
                  color="primary"
                  startIcon={<Visibility />}
                  onClick={() => handleOpenModal(user)}
                  sx={{
                    textTransform: 'none',
                    '&:hover': { backgroundColor: '#3f51b5' },
                    borderRadius: 2,
                  }}
                >
                  View
                </Button>
              ),
            }))}
          />
        )}

        {/* Modal (Dialog) for viewing Horoscope */}
        <Dialog open={open} onClose={handleCloseModal} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold', fontSize: '1.2rem' }}>
            Horoscope Data
            <IconButton
              edge="end"
              color="inherit"
              onClick={handleCloseModal}
              aria-label="close"
              sx={{ position: 'absolute', right: 8, top: 8 }}
            >
              <Close />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ backgroundColor: '#fafafa' }}>
            {selectedUser && selectedUser.horoscopeData ? (
              <Box>
                <Typography variant="h6" sx={{ marginBottom: '10px', color: '#3f51b5' }}>
                  <strong>Name:</strong> {selectedUser.username}
                </Typography>

                {/* Card Layout for Horoscope Data */}
                <Card sx={{ marginBottom: 2, borderRadius: 2, backgroundColor: '#f1f1f1' }}>
                  <CardContent>
                    <Grid container spacing={2}>
                      {/* Phone Number */}
                      <Grid item xs={6}>
                        <Box display="flex" alignItems="center">
                          <Phone sx={{ marginRight: 1 }} color="primary" />
                          <Typography variant="body2" sx={{ fontSize: '0.875rem', color: '#555' }}>
                            <strong>Phone Number:</strong> {selectedUser.phoneNumber}
                          </Typography>
                        </Box>
                      </Grid>

                      {/* Email */}
                      <Grid item xs={6}>
                        <Box display="flex" alignItems="center">
                          <Email sx={{ marginRight: 1 }} color="primary" />
                          <Typography variant="body2" sx={{ fontSize: '0.875rem', color: '#555' }}>
                            <strong>Email:</strong> {selectedUser.email}
                          </Typography>
                        </Box>
                      </Grid>

                      {/* Date of Birth */}
                      <Grid item xs={6}>
                        <Box display="flex" alignItems="center">
                          <Cake sx={{ marginRight: 1 }} color="primary" />
                          <Typography variant="body2" sx={{ fontSize: '0.875rem', color: '#555' }}>
                            <strong>DOB:</strong> {moment(selectedUser.horoscopeData[0]?.dob).format('YYYY-MM-DD')}
                          </Typography>
                        </Box>
                      </Grid>

                      {/* Zodiac */}
                      <Grid item xs={6}>
                        <Box display="flex" alignItems="center">
                          <AccessTime sx={{ marginRight: 1 }} color="primary" />
                          <Typography variant="body2" sx={{ fontSize: '0.875rem', color: '#555' }}>
                            <strong>Zodiac:</strong> {selectedUser.horoscopeData?.zodiac}
                          </Typography>
                        </Box>
                      </Grid>

                      {/* Horoscope Time */}
                      <Grid item xs={6}>
                        <Box display="flex" alignItems="center">
                          <AccessTime sx={{ marginRight: 1 }} color="primary" />
                          <Typography variant="body2" sx={{ fontSize: '0.875rem', color: '#555' }}>
                            <strong>Time:</strong> {formattedTime}
                          </Typography>
                        </Box>
                      </Grid>

                      {/* Location */}
                      <Grid item xs={6}>
                        <Box display="flex" alignItems="center">
                          <LocationOn sx={{ marginRight: 1 }} color="primary" />
                          <Typography variant="body2" sx={{ fontSize: '0.875rem', color: '#555' }}>
                            <strong>Place:</strong> {selectedUser.horoscopeData?.place}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>

              </Box>
            ) : (
              <Typography variant="body1">No horoscope data available.</Typography>
            )}
          </DialogContent>
          <DialogActions sx={{ backgroundColor: '#f5f5f5' }}>
            <Button
              onClick={handleCloseModal}
              sx={{
                backgroundColor: '#ff3d00',
                color: '#fff',
                '&:hover': { backgroundColor: '#d32f2f', color: '#fff' },
              }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </DashboardLayout>
  );
}

export default Users;
